/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React from 'react';
import { css, StyleSheet } from 'aphrodite';
import { matchPath, useHistory, useLocation } from 'react-router';

import { RouterStates } from '../../../definitions/RouterStates';
import { Error, Footer, Header, Status, Table, Wrapper } from '../../../components/common/Pipeline';
import { Tabs } from '../../../components/common/enterprise/Tabs/Tabs';
import { NotaryAPI } from '../../../api/notary/types';
import { usePermissions } from '../../../hooks/usePermissions';
import { RoomsPipelineFilter } from '../Rooms/RoomsPipelineFilter/RoomsPipelineFilter';
import { getColumnConfigurations } from './configs';
import { defaultPipelineFilters } from '@utils/filters';
import { FilterParams } from '@shared/types/pipeline';
import { openRoomInNewWindow } from '@utils/routing';
import { isEmpty } from '@utils/lodash';

export namespace AssignmentsPipeline {
  export interface Props {
    rooms: NotaryAPI.RoomListItem[];
    isLoading: boolean;
    hasError: boolean;
    page: number;
    totalCount: number;
    fetchRooms: (filters: FilterParams) => Promise<void>;
    setPage: (page: number) => void;
    filters: FilterParams;
    setFilters: (params: FilterParams) => void;
  }
}

const styles = StyleSheet.create({
  tabsWrapper: {
    marginLeft: 40,
  },
  filterWrapper: {
    display: 'flex',
    justifyContent: 'right',
    marginRight: 40,
  },
});

export const AssignmentsPipeline: React.FC<AssignmentsPipeline.Props> = ({
  rooms,
  isLoading,
  hasError,
  page,
  totalCount,
  fetchRooms,
  setPage,
}) => {
  const history = useHistory();
  const location = useLocation();
  const permissions = usePermissions();
  const hasSuperAdminPermission = usePermissions().includes(NotaryAPI.BSRPermissions.BSRSuperAdmin);
  const hasAccessToRoomListOfGroup = permissions?.includes(NotaryAPI.BSRPermissions.BSRViewRoomList);

  const activeTab = matchPath(location.pathname, { path: RouterStates.ROOMS, exact: true })
    ? 'otherRooms'
    : 'myAppointments';

  const tabsConfig = [
    { id: 'myAppointments', label: 'Assigned To Me', onClick: () => history.push(RouterStates.ROOM_ASSIGNMENTS) },
  ];

  if (hasAccessToRoomListOfGroup) {
    tabsConfig.push({ id: 'otherRooms', label: 'All Notarizations', onClick: () => history.push(RouterStates.ROOMS) });
  }

  return (
    <Wrapper>
      <Header header="Notarizations" />
      <div className={css(styles.filterWrapper)} id="it-filter">
        <RoomsPipelineFilter getAssignedRooms={true} />
      </div>
      <div className={css(styles.tabsWrapper)} data-it="it-tabHeader">
        <Tabs activeTab={activeTab} tabsConfig={tabsConfig} />
      </div>
      <Table
        data={rooms}
        onRowClick={room => {
          openRoomInNewWindow(room.id);
        }}
        tableColumns={getColumnConfigurations(fetchRooms, defaultPipelineFilters(hasSuperAdminPermission))}
      />
      <Footer page={page} onPageChange={setPage} totalCount={totalCount} />
      {hasError && <Error />}
      <Status isLoading={isLoading} isEmpty={isEmpty(rooms)} emptyMessage={'No appointments here at the moment.'} />
    </Wrapper>
  );
};
